// src/utils/adRequestFilter.js
//
// "Ad requests only" filter for the Raw requests tab (Slot Story step 4,
// docs/slot-story-spec.md; requirements D10). Presentation only: the filter is
// applied to the TABLE render. Hidden rows stay in state, HAR export, the Checks
// input and the lifecycle input.
//
// Visibility is the Checks ad-scope rule, not a hand-written type list:
//   isAdRequest(row) = classifyAdTraffic(row).adCategory is in AD_RELEVANT
//                      (validation/adRelevance.js, twin of the proxy), OR the
//                      request type is a lifecycle-input type.
// `unknown` stays visible (unrecognised ad vendors), as in Checks.
// A row referenced by any Slots step is never hidden.

import { isAdRelevant } from '../validation/adRelevance';
import { slotRowKeys } from './slotHar';

export const REQUESTS_AD_ONLY_KEY = 'requests.adOnly';

/** Request types the lifecycle assemblers read; always visible (omid_* by prefix). */
export const LIFECYCLE_INPUT_TYPES = new Set([
  'google', 'google_impression', 'google_click',
  'prebid', 'prebid_win',
  'amazon', 'amazon_video', 'amazon_impression',
  'bidder_impression', 'ima', 'video_content', 'sdk_bidding',
]);

/** Default ON. Only the literal string 'false' resolves to OFF (mirrors adOnlyScope.js). */
export function resolveRequestsAdOnly(stored) {
  return stored !== 'false';
}

export function readRequestsAdOnly() {
  try {
    return resolveRequestsAdOnly(localStorage.getItem(REQUESTS_AD_ONLY_KEY));
  } catch {
    return true;
  }
}

export function writeRequestsAdOnly(adOnly) {
  try {
    localStorage.setItem(REQUESTS_AD_ONLY_KEY, adOnly ? 'true' : 'false');
  } catch {
    // best-effort persistence only
  }
}

function typeOf(row) {
  const t = row && (row.requestType || row.type);
  return typeof t === 'string' ? t.toLowerCase() : '';
}

export function isLifecycleInputType(row) {
  const t = typeOf(row);
  return !!t && (LIFECYCLE_INPUT_TYPES.has(t) || t.startsWith('omid_'));
}

/** True when the row is ad traffic (or feeds the ad lifecycle). Never throws. */
export function isAdRequest(row) {
  try {
    if (isLifecycleInputType(row)) return true;
    return isAdRelevant(row);
  } catch {
    return false;
  }
}

/** Every row key any slot step / mediation bill / OMID beacon points at. */
export function slotReferencedKeys(lifecycles) {
  const out = new Set();
  for (const lc of Array.isArray(lifecycles) ? lifecycles : []) {
    try {
      for (const k of slotRowKeys(lc)) out.add(k);
    } catch { /* a malformed lifecycle contributes nothing */ }
  }
  return out;
}

const defaultKeyOf = (r) => (r ? (r._k != null ? r._k : (r.requestId != null ? r.requestId : r.id)) : undefined);

/**
 * partitionAdRequests(rows, { adOnly, protectedKeys, keyOf })
 *   -> { visible, hidden, hiddenCount, noiseCount }
 * `noiseCount` = rows that fail isAdRequest and are not slot-referenced (what the
 * filter would hide). `hidden`/`hiddenCount` are that set when adOnly is on, else
 * empty / 0. Never mutates `rows`.
 */
export function partitionAdRequests(rows, opts = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const adOnly = opts.adOnly !== false;
  const keep = opts.protectedKeys instanceof Set ? opts.protectedKeys : new Set();
  const keyOf = typeof opts.keyOf === 'function' ? opts.keyOf : defaultKeyOf;

  const noise = [];
  const visible = [];
  for (const r of list) {
    const isNoise = !isAdRequest(r) && !keep.has(keyOf(r));
    if (isNoise) noise.push(r);
    if (!(adOnly && isNoise)) visible.push(r);
  }
  return {
    visible,
    hidden: adOnly ? noise : [],
    hiddenCount: adOnly ? noise.length : 0,
    noiseCount: noise.length,
  };
}

/** True when showing `row` in the table needs the filter lifted (Open request, jumps). */
export function needsUnhide(row, adOnly, protectedKeys, keyOf = defaultKeyOf) {
  if (!adOnly || !row) return false;
  const keep = protectedKeys instanceof Set ? protectedKeys : new Set();
  return !isAdRequest(row) && !keep.has(keyOf(row));
}
